import { useEffect, useMemo } from 'react';
import { useBelowMd } from '../../lib/useBelowMd';
import { MagnifyingGlass, ShoppingBag } from '@phosphor-icons/react';
import type {
  ShopifyCampaignCoupon,
  ShopifyCouponTerms,
} from '@discovr/features/shopify';
import type { PublicShopifyCoupon, PublicShopifyQuery } from '@discovr/features';
import { Tooltip, TooltipTrigger, TooltipContent } from '../../components/ui/tooltip';
import { DataTable, type ColumnDef } from '../../components/common/DataTable';
import { EmptyState } from '../../components/common/EmptyState';
import { QueryErrorState } from '../../components/common/QueryErrorState';
import { Input } from '../../components/ui/input';
import { PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE } from '../useCampaignPageSizeParam';
import { PAGED_TABLE_MAX_SIZE } from '../pagedTableState';
import { PhaseSubTabs } from './parts/PhaseSubTabs';
import { appliesToTitleAndNames, couponTermsTooltip } from './shopifyCodeUtils';
import { couponValueLabel } from './shopifyDiscountKindSummary';
import { resolveCurrencyCode } from './shopifyCurrency';
import { COUPON_LIVE_FILTER_TABS, type CouponLiveFilter } from './shopifyCouponFilters';
import {
  CreatorCell,
  ShopifyCodeCell,
  TrackingLinkCell,
  CouponStatusBadge,
} from './shopifyCouponCells';
import { usePublicView } from '../publicView';
import { shopifyCopy } from '@discovr/features/copy';
import { ShopifyDataRemovedNotice } from '../../integrations/ShopifyDataRemovedNotice';

/** "Applies to" cell — see ShopifySettingsTab's AppliesToCell for why this is
 *  its own component. No modal here (this view is read-only, no click
 *  affordance), so the hover tooltip carries the full Minimum/Combines/Active
 *  breakdown — the only way a public viewer sees that detail at all. Always
 *  available on hover: this is informational content the viewer wants
 *  regardless of whether the title/names line happens to be visually
 *  truncated, not a mere ellipsis hint — gating it on truncation (the
 *  previous behavior) meant it never appeared for the common case of a
 *  short product/collection name that already fits the column. */
function PublicAppliesToCell({
  c,
  storeCurrencyCode,
  storeTimezone,
}: {
  c: ShopifyCampaignCoupon;
  storeCurrencyCode?: string;
  storeTimezone?: string;
}) {
  // Shared with ShopifySettingsTab's AppliesToCell — see
  // appliesToTitleAndNames's doc comment for why this must be one source.
  const { title, names } = appliesToTitleAndNames(c.terms);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div tabIndex={0} className="min-w-0">
          <span className="text-body-sm-regular text-foreground block truncate">{title}</span>
          {names && <span className="text-muted-foreground block truncate text-caption-regular">{names}</span>}
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <div className="whitespace-pre-line">{couponTermsTooltip(c.terms, storeCurrencyCode, storeTimezone)}</div>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Bridges the public share-view's PublicShopifyCoupon (no uses_count — see
 * that type's own doc comment for why) to
 * ShopifyCampaignCoupon, the type CreatorCell/TrackingLinkCell/DataTable's
 * generic actually expect. The two placeholder fields are never read by
 * either cell. shopify_state is absent from the public payload too, so the
 * public badge reads the terms dates instead (the one date-fallback path).
 * This exists purely so the shared cell components stay
 * completely untouched (no public-mode branch inside them) rather than
 * threading an optional-field variant through code the authed table also
 * uses.
 */
function toShopifyCampaignCoupon(c: PublicShopifyCoupon): ShopifyCampaignCoupon {
  return {
    ...c,
    uses_count: null,
    uses_fetched_at: null,
    paid_orders_count: 0,
    terms: c.terms as unknown as ShopifyCouponTerms,
  } as ShopifyCampaignCoupon;
}

// CreatorCell/TrackingLinkCell/getCouponStatus come from shopifyCouponCells.tsx
// (a small, mutation-free shared file), never from ShopifySettingsTab.tsx
// directly — see that file's own doc comment for why: a static import from
// ShopifySettingsTab would pull its entire mutation surface into this
// public bundle, unreachable but still shipped.

/**
 * PublicShopifyCouponsTab — the "shopify" tab's public payload, Coupons
 * sub-view only (the tab's other sub-view, Settings, has no public
 * rendering path at all — see publicShopifyView's doc comment in
 * share_view_page.go). A deliberately SEPARATE, small component from
 * ShopifySettingsTab rather than a public-mode branch threaded through it:
 * that component is 1000+ lines of live mutations (Generate, replace,
 * config-edit, invalidate, bulk-invalidate); gating this at the top, in one
 * small file with no mutation call sites at all, keeps "nothing here can
 * ever write" an auditable property of the file rather than something that
 * has to be verified conditional-by-conditional.
 *
 * Full parity with the authed Coupons table — including the coupon CODE
 * (2026-08-21 product decision) — minus the Actions column (replace/
 * config-edit/invalidate) and the bulk-select toolbar, which have no
 * public-mode counterpart, and the "N new creators pending" Generate nudge,
 * which is a write affordance.
 */
const FALLBACK_QUERY: PublicShopifyQuery = {
  page: 1,
  limit: DEFAULT_PAGE_SIZE,
  live: 'all',
  search: '',
};

const NO_OP = () => {};

export function PublicShopifyCouponsTab() {
  const {
    shopifyCoupons,
    shopifyQuery = FALLBACK_QUERY,
    setShopifyQuery = NO_OP,
    shopifySearchInput,
    setShopifySearchInput = NO_OP,
    shopifyPageLoading = false,
    shopifyPageError = false,
    retryShopifyPage,
  } = usePublicView();
  // Read before the early return below so the columns memo can depend on it.
  const storeCurrencyCode = shopifyCoupons?.status.currency_code;
  const storeTimezone = shopifyCoupons?.status.store_timezone;

  // The server already filtered and paged these; nothing here narrows them.
  const coupons = useMemo(
    () => (shopifyCoupons?.coupons ?? []).map(toShopifyCampaignCoupon),
    [shopifyCoupons],
  );
  const pagination = shopifyCoupons?.pagination;
  // A page past the end comes back empty with the real total; go to the last
  // page that exists, once the page on screen is the one that was asked for.
  const lastPage = pagination ? Math.max(1, pagination.total_pages) : undefined;
  useEffect(() => {
    if (lastPage === undefined || shopifyPageLoading) return;
    if (shopifyQuery.page > lastPage) setShopifyQuery({ page: lastPage });
  }, [lastPage, shopifyPageLoading, shopifyQuery.page, setShopifyQuery]);

  const narrow = useBelowMd();
  const columns = useMemo(
    (): ColumnDef<ShopifyCampaignCoupon>[] => [
      {
        id: 'creator',
        header: 'Creator',
        minWidth: narrow ? 150 : 220,
        sticky: 'left',
        cellClassName: 'whitespace-normal',
        cell: (c) => <CreatorCell c={c} />,
      },
      {
        id: 'code',
        header: 'Code',
        // Was 140 when Status/Discount were their own columns — see the
        // authed ShopifySettingsTab's identical fix for why that overflowed.
        // Status + discount now live as a sub-line in this same cell
        // (Discovr's "two-line cells: value + sub-label" table convention).
        minWidth: 220,
        cell: (c) => {
          return (
            <div className="min-w-0">
<ShopifyCodeCell code={c.code} chip />
              <div className="text-muted-foreground mt-1 flex items-center gap-1.5 text-caption-regular">
                <CouponStatusBadge
                  status={c.status}
                  endDate={c.terms.end_date}
                  startDate={c.terms.start_date}
                />
                <span aria-hidden="true">·</span>
                <span>
                  {couponValueLabel(
                    c.terms,
                    c.discount_pct,
                    resolveCurrencyCode(c.terms.currency_code, storeCurrencyCode),
                  )}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        id: 'terms',
        header: 'Applies to',
        minWidth: 200,
        maxWidth: 280,
        // See ShopifySettingsTab's identical "terms" column for why the
        // second line only shows for collections/multi-product coupons —
        // a single product already names itself on the summary line.
        cell: (c) => <PublicAppliesToCell c={c} storeCurrencyCode={storeCurrencyCode} storeTimezone={storeTimezone} />,
      },
      {
        id: 'link',
        header: 'Link',
        minWidth: 200,
        cell: (c) => <TrackingLinkCell c={c} />,
      },
    ],
    [storeCurrencyCode, storeTimezone, narrow],
  );

  if (!shopifyCoupons) return null;
  const { status, pagination: shownPage } = shopifyCoupons;
  const isFiltering = shopifyQuery.live !== 'all' || shopifyQuery.search.trim() !== '';
  if (status.store_state === 'removed') {
    return (
      <div className="space-y-5">
        <h2 className="text-heading-h5 text-foreground font-semibold">Shopify</h2>
        <ShopifyDataRemovedNotice detail={shopifyCopy.state.removed.publicBody} />
      </div>
    );
  }
  const storeLine =
    status.store_state === 'store_unavailable'
      ? shopifyCopy.state.store_unavailable.badge
      : status.connected && status.shop_domain
        ? `Connected to ${status.shop_domain}`
        : 'Not connected';

  return (
    <div className="space-y-5" data-clarity-mask="True">
      <div className="flex items-center gap-2">
        <ShoppingBag className="text-muted-foreground size-5" aria-hidden="true" />
        <div>
          <h2 className="text-heading-h5 text-foreground font-semibold">Shopify</h2>
          <p className="text-body-sm-regular text-muted-foreground">
            {storeLine}{' '}
            · {status.live_coupon_count} active code
            {status.live_coupon_count === 1 ? '' : 's'}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-xs flex-1">
          <MagnifyingGlass
            aria-hidden="true"
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
          />
          <Input
            value={shopifySearchInput ?? shopifyQuery.search}
            onChange={(e) => setShopifySearchInput(e.target.value)}
            aria-label="Search creator or code"
            placeholder="Search creator or code"
            className="pl-8"
          />
        </div>
        <PhaseSubTabs<CouponLiveFilter>
          bare
          active={shopifyQuery.live}
          tabs={COUPON_LIVE_FILTER_TABS}
          onChange={(live) => setShopifyQuery({ live, page: 1 })}
        />
      </div>

      {shopifyPageError ? (
        <QueryErrorState message="Couldn't load this page of discount codes." onRetry={retryShopifyPage} />
      ) : (
        <div aria-busy={shopifyPageLoading}>
          <span role="status" className="sr-only">
            {shopifyPageLoading ? 'Loading discount codes…' : ''}
          </span>
          <DataTable
            columns={columns}
            data={coupons}
            ariaLabel="Discount codes"
            getRowId={(c) => c.id}
            emptyState={
              <EmptyState
                title={isFiltering ? 'No discount codes match your filters.' : 'No discount codes yet.'}
                className="py-10"
              />
            }
            pagination={{
              page: shownPage.page,
              pageSize: shopifyQuery.limit,
              total: shownPage.total,
              totalPages: shownPage.total_pages,
              onPageChange: (page) => setShopifyQuery({ page }),
              onPageSizeChange: (limit) => setShopifyQuery({ limit, page: 1 }),
              pageSizeOptions: PAGE_SIZE_OPTIONS,
              maxPageSize: PAGED_TABLE_MAX_SIZE,
              noun: 'discount codes',
              keepWhenEmpty: isFiltering && !shopifyPageLoading,
            }}
          />
        </div>
      )}
    </div>
  );
}
